import { memo, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Cell,
} from 'recharts';
import { ChartWrapper } from './ChartWrapper';
import { NoChartData } from './NoChartData';
import { LABEL_STYLE } from './constants';
import { useApp } from '../../../context/AppContext';
import { TYPE_TO_LEVEL } from '../../../utils/dataProcessor/constants';

const LEVEL_ORDER = ['Output', 'Outcome', 'Impact'];

const TYPE_COLORS = {
  'Knowledge product': '#006837',
  'Innovation development': '#2E8B57',
  'Capacity sharing for development': '#3CB371',
  'Other output': '#66CDAA',
  'Innovation use': '#007DBC',
  'Policy change': '#2196F3',
  'Other outcome': '#64B5F6',
  'Innovation Package': '#90CAF9',
  'Capacity change': '#42A5F5',
  'Complementary innovation': '#BBDEFB',
  'Impact contribution': '#8B5CF6',
};

const LEVEL_COLORS = { Output: '#006837', Outcome: '#007DBC', Impact: '#8B5CF6' };

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white text-gray-800 text-xs rounded-lg px-3 py-2 shadow-lg border border-gray-200">
      <div className="font-semibold mb-1">{d.type}</div>
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: d.color }} />
        Count: {d.count}
      </div>
      <div className="text-gray-500 mt-0.5">{d.level}</div>
    </div>
  );
};

export const ResultsLevelChart = memo(function ResultsLevelChart({ data: dataProp, sourceResults }) {
  const { stats, filteredResults } = useApp();
  const results = sourceResults ?? filteredResults;

  const chartData = useMemo(() => {
    const src = results ?? [];
    if (!src.length) return [];

    // Count per level × type
    const counts = {};
    for (const r of src) {
      const level = r.level || TYPE_TO_LEVEL[r.type] || 'Other';
      const type = r.type || 'Unknown';
      if (!counts[level]) counts[level] = {};
      counts[level][type] = (counts[level][type] || 0) + 1;
    }

    // Flatten into one row per type, grouped by level, sorted descending within level
    const rows = [];
    for (const level of LEVEL_ORDER) {
      if (!counts[level]) continue;
      const types = Object.entries(counts[level])
        .sort((a, b) => b[1] - a[1]);
      for (const [type, count] of types) {
        rows.push({
          type,
          label: type.length > 24 ? type.substring(0, 22) + '...' : type,
          level,
          count,
          color: TYPE_COLORS[type] || LEVEL_COLORS[level] || '#94a3b8',
        });
      }
    }
    return rows;
  }, [results]);

  if (!chartData.length) return (
    <ChartWrapper title="Output vs Outcome Breakdown">
      <NoChartData message="No results data available" detail="This chart shows results broken down by level (Output, Outcome, Impact) and type. Load data or adjust filters to populate." />
    </ChartWrapper>
  );

  // Group markers for level labels on the left
  const levelGroups = [];
  let i = 0;
  for (const level of LEVEL_ORDER) {
    const count = chartData.filter(r => r.level === level).length;
    if (count > 0) {
      levelGroups.push({ level, start: i, count });
      i += count;
    }
  }

  const barHeight = 32;
  const groupGap = 16;
  const totalHeight = chartData.length * barHeight + levelGroups.length * groupGap + 40;

  return (
    <ChartWrapper title="Output vs Outcome Breakdown" info="Results grouped by level (Output, Outcome, Impact), showing the count for each result type." chartData={chartData} sourceResults={results} exportFilename="results-by-level">
      <div className="flex">
        {/* Level group labels */}
        <div className="flex flex-col justify-start" style={{ width: 64, paddingTop: 8 }}>
          {levelGroups.map(({ level, count }, gi) => (
            <div
              key={level}
              className="flex items-center justify-end pr-2"
              style={{
                height: count * barHeight,
                marginTop: gi === 0 ? 0 : groupGap,
              }}
            >
              <span
                className="text-xs font-bold writing-mode-vertical"
                style={{
                  color: LEVEL_COLORS[level],
                  writingMode: count > 1 ? 'vertical-rl' : undefined,
                  transform: count > 1 ? 'rotate(180deg)' : undefined,
                  letterSpacing: count > 1 ? '0.05em' : undefined,
                }}
              >
                {level}
              </span>
            </div>
          ))}
        </div>
        {/* Chart */}
        <div className="flex-1">
          <ResponsiveContainer width="100%" height={totalHeight}>
            <BarChart data={chartData} layout="vertical" barCategoryGap={4} margin={{ left: 0, right: 40 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={160} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
                <LabelList dataKey="count" position="right" fill={LABEL_STYLE.fill} fontSize={LABEL_STYLE.fontSize} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Compact legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-2">
        {levelGroups.map(({ level }) => (
          <div key={level} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: LEVEL_COLORS[level] }} />
            <span className="text-xs text-gray-600 font-medium">{level}</span>
          </div>
        ))}
      </div>
    </ChartWrapper>
  );
});
